import { ElementRef } from '@angular/core';
import { startMomentumScrolling as runMomentumScroll, stopMomentumScrolling as stopMomentumScroll, applyHorizontalDragMove } from '../../shared/utils/momentum-scroll.util';

export interface DragScrollConfig {
  scrollWrapper: ElementRef;
  isDragging: boolean;
  /** Pointer down recorded, but drag-scroll not yet activated (below movement threshold). */
  dragPending: boolean;
  startX: number;
  startY: number;
  scrollLeft: number;
  lastX: number;
  velocityX: number;
  lastMoveTime: number;
  momentumAnimation: number | null;
  scrollDirection: 'horizontal' | 'vertical' | null;
  hasDeterminedDirection: boolean;
}

export class TableUtils {
  /** Pixels of pointer movement before drag-to-scroll takes over (keeps click/double-click select working). */
  private static readonly DRAG_ACTIVATION_THRESHOLD_PX = 6;

  /**
   * Factory method – creates a default DragScrollConfig for a given scroll wrapper.
   * Eliminates the repeated config-object literal across all table components.
   */
  static createDragScrollConfig(scrollWrapper: ElementRef): DragScrollConfig {
    return {
      scrollWrapper,
      isDragging: false,
      dragPending: false,
      startX: 0,
      startY: 0,
      scrollLeft: 0,
      lastX: 0,
      velocityX: 0,
      lastMoveTime: 0,
      momentumAnimation: null,
      scrollDirection: null,
      hasDeterminedDirection: false
    };
  }

  /**
   * Removes the 'dragging' class from the standard table scroll wrapper.
   * Shared cleanup logic across all table components.
   */
  static cleanupTableScrolling(): void {
    const tableWrapper = document.querySelector('.table-scroll-wrapper');
    if (tableWrapper) {
      tableWrapper.classList.remove('dragging');
    }
  }

  /**
   * Cleanup, create config, and initialize drag scrolling for a table scroll wrapper.
   * Returns the new config, or null if wrapper is missing.
   */
  static reinitializeDragScrolling(
    scrollWrapper: ElementRef<HTMLElement> | undefined
  ): DragScrollConfig | null {
    if (!scrollWrapper?.nativeElement) return null;
    TableUtils.cleanupTableScrolling();
    const config = TableUtils.createDragScrollConfig(scrollWrapper);
    TableUtils.initializeDragScrolling(config);
    return config;
  }

  /**
   * Check if an element is interactive (should not trigger drag)
   */
  static isInteractiveElement(target: HTMLElement): boolean {
    const interactiveElements = ['BUTTON', 'A', 'INPUT', 'SELECT'];
    const interactiveClasses = ['.action-button', '.order-link', '.menu-trigger'];
    
    return interactiveElements.includes(target.tagName) || 
           target.closest('button') !== null ||
           target.closest('a') !== null ||
           interactiveClasses.some(cls => target.closest(cls));
  }

  /**
   * Initialize drag scrolling for a table.
   * Text selection (e.g. double-click license plate) works until the pointer moves
   * past DRAG_ACTIVATION_THRESHOLD_PX, then horizontal drag-scroll activates.
   */
  static initializeDragScrolling(config: DragScrollConfig): void {
    const scrollWrapper = config?.scrollWrapper?.nativeElement;
    if (!scrollWrapper) return;
    
    // Mouse events
    scrollWrapper.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return; // Only left-click
      if (TableUtils.isInteractiveElement(e.target as HTMLElement)) return;
      TableUtils.beginPointerGesture(e.pageX, e.pageY, config);
    });

    scrollWrapper.addEventListener('mousemove', (e: MouseEvent) => {
      if (!config.dragPending && !config.isDragging) return;

      if (config.dragPending && !config.isDragging) {
        if (!TableUtils.shouldActivateDrag(e.pageX, e.pageY, config)) {
          return;
        }
        TableUtils.activateDrag(config);
      }

      if (!config.isDragging) return;
      e.preventDefault();
      TableUtils.handleDragMove(e.pageX, e.pageY, config);
    });

    scrollWrapper.addEventListener('mouseup', () => {
      TableUtils.handleDragEnd(config);
    });

    scrollWrapper.addEventListener('mouseleave', () => {
      TableUtils.handleDragEnd(config);
    });

    // Touch events with direction detection
    scrollWrapper.addEventListener('touchstart', (e: TouchEvent) => {
      if (TableUtils.isInteractiveElement(e.target as HTMLElement)) return;
      TableUtils.beginPointerGesture(e.touches[0].pageX, e.touches[0].pageY, config);
    });

    scrollWrapper.addEventListener('touchmove', (e: TouchEvent) => {
      if (!config.dragPending && !config.isDragging) return;
      
      const touch = e.touches[0];

      if (config.dragPending && !config.isDragging) {
        if (!TableUtils.shouldActivateDrag(touch.pageX, touch.pageY, config)) {
          return;
        }
        TableUtils.activateDrag(config);
      }

      if (!config.isDragging) return;
      
      // Determine scroll direction on first move after activation
      if (!config.hasDeterminedDirection) {
        const deltaX = Math.abs(touch.pageX - config.startX);
        const deltaY = Math.abs(touch.pageY - config.startY);
        
        // Only determine direction if movement is significant enough
        if (deltaX > 5 || deltaY > 5) {
          config.scrollDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
          config.hasDeterminedDirection = true;
        }
      }
      
      // Only prevent default and handle horizontal drag if scrolling horizontally
      if (config.scrollDirection === 'horizontal') {
        e.preventDefault();
        TableUtils.handleDragMove(touch.pageX, touch.pageY, config);
      } else if (config.scrollDirection === 'vertical') {
        // Allow vertical scrolling - end the drag operation
        TableUtils.handleDragEnd(config);
      }
    }, { passive: false });

    scrollWrapper.addEventListener('touchend', () => {
      TableUtils.handleDragEnd(config);
    });

    // Prevent default drag behavior on images and links within the table
    scrollWrapper.addEventListener('dragstart', (e: Event) => e.preventDefault());
  }

  /** Record pointer-down without blocking text selection yet. */
  private static beginPointerGesture(pageX: number, pageY: number, config: DragScrollConfig): void {
    TableUtils.stopMomentumAnimation(config);
    const el = config.scrollWrapper.nativeElement;
    const x = pageX - el.offsetLeft;
    config.dragPending = true;
    config.isDragging = false;
    config.startX = x;
    config.startY = pageY;
    config.scrollLeft = el.scrollLeft;
    config.lastX = x;
    config.lastMoveTime = Date.now();
    config.velocityX = 0;
    config.scrollDirection = null;
    config.hasDeterminedDirection = false;
  }

  private static shouldActivateDrag(pageX: number, pageY: number, config: DragScrollConfig): boolean {
    const currentX = pageX - config.scrollWrapper.nativeElement.offsetLeft;
    const deltaX = Math.abs(currentX - config.startX);
    const deltaY = Math.abs(pageY - config.startY);
    return (
      deltaX > TableUtils.DRAG_ACTIVATION_THRESHOLD_PX ||
      deltaY > TableUtils.DRAG_ACTIVATION_THRESHOLD_PX
    );
  }

  private static activateDrag(config: DragScrollConfig): void {
    config.dragPending = false;
    config.isDragging = true;
    config.scrollWrapper.nativeElement.classList.add('dragging');
    config.scrollWrapper.nativeElement.style.cursor = 'grabbing';
    config.scrollWrapper.nativeElement.style.userSelect = 'none';
    // Clear any partial selection once drag-scroll takes over
    window.getSelection()?.removeAllRanges();
  }

  private static handleDragMove(pageX: number, pageY: number, config: DragScrollConfig): void {
    if (!config.isDragging) return;
    applyHorizontalDragMove(pageX, config);
  }

  private static handleDragEnd(config: DragScrollConfig): void {
    const wasDragging = config.isDragging;
    config.dragPending = false;

    if (wasDragging) {
      config.isDragging = false;
      config.scrollWrapper.nativeElement.classList.remove('dragging');
      config.scrollWrapper.nativeElement.style.cursor = '';
      config.scrollWrapper.nativeElement.style.userSelect = '';
      
      // Start momentum scrolling if there's sufficient velocity
      TableUtils.startMomentumScrolling(config);
    }
  }

  private static startMomentumScrolling(config: DragScrollConfig): void {
    runMomentumScroll(config);
  }

  private static stopMomentumAnimation(config: DragScrollConfig): void {
    stopMomentumScroll(config);
  }

  /**
   * Returns the correct sort icon asset path for a given column.
   * Shared across all orders-list components.
   */
  static getSortIcon(
    sortColumn: string | null,
    sortDirection: 'asc' | 'desc' | 'none',
    column: string
  ): string {
    if (sortColumn === column) {
      const iconMap: Record<string, string> = {
        orderId: sortDirection === 'asc'
          ? '/assets/images/sort-ascending-numbers.svg'
          : '/assets/images/sort-descending-numbers.svg',
        dateTime: sortDirection === 'asc'
          ? '/assets/images/time_sort_ascending.png'
          : '/assets/images/time_sort_descending.png'
      };
      return iconMap[column] ?? '/assets/images/tabler_arrows_sort.png';
    }
    return '/assets/images/tabler_arrows_sort.png';
  }

  /**
   * Toggle sort column/direction and return sorted copy of orders. Shared by admin-dashboard sortByOrderId/sortByDateTime.
   */
  static applyOrdersSort<T>(
    orders: T[],
    currentColumn: string,
    currentDirection: string,
    clickedColumn: string,
    compare: (a: T, b: T) => number
  ): { sortedOrders: T[]; sortColumn: string; sortDirection: string } {
    const isAsc = currentDirection === 'asc';
    const nextDirection =
      currentColumn === clickedColumn ? (isAsc ? 'desc' : 'asc') : 'asc';
    const nextColumn = clickedColumn;
    const sorted = [...orders].sort((a, b) => {
      const cmp = compare(a, b);
      return nextDirection === 'asc' ? cmp : -cmp;
    });
    return { sortedOrders: sorted, sortColumn: nextColumn, sortDirection: nextDirection };
  }

  /**
   * Positions a floating filter modal beneath the column header that triggered it.
   * Shared across all orders-list components.
   */
  static positionModal(event: Event, headerSelector: string, modalSelector: string): void {
    setTimeout(() => {
      const header = (event.target as HTMLElement).closest(headerSelector) as HTMLElement;
      const modal = document.querySelector(modalSelector) as HTMLElement;

      if (header && modal) {
        const rect = header.getBoundingClientRect();
        const filterIcon = header.querySelector('.filter-icon') as HTMLElement;
        let iconLeft = rect.left;

        if (filterIcon) {
          const iconRect = filterIcon.getBoundingClientRect();
          iconLeft = iconRect.left;
        }

        modal.style.top = `${rect.bottom + 5}px`;
        modal.style.left = `${iconLeft}px`;
      }
    }, 0);
  }
}
